import * as React from 'react';

import { Box, Dialog as ChakraDialog, Portal } from '@chakra-ui/react';
import { ArrowBack, Close } from '@m/icons';

import { CloseButton } from './close-button';
import { NavigationButton } from './navigation-button';

interface DialogContentProps extends ChakraDialog.ContentProps {
  backdrop?: boolean;
  children?: React.ReactNode;
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement>;
}

export const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>((props, ref) => {
  const { children, portalled = true, portalRef, backdrop = true, ...rest } = props;

  return (
    <Portal disabled={!portalled} container={portalRef}>
      {backdrop && <ChakraDialog.Backdrop />}
      <ChakraDialog.Positioner>
        <ChakraDialog.Content ref={ref} {...rest} asChild={false}>
          {children}
        </ChakraDialog.Content>
      </ChakraDialog.Positioner>
    </Portal>
  );
});

export const DialogCloseTrigger = React.forwardRef<
  HTMLButtonElement,
  ChakraDialog.CloseTriggerProps
>((props, ref) => (
  <ChakraDialog.CloseTrigger position="absolute" top="6" insetEnd="6" {...props} asChild>
    <CloseButton size="xs" ref={ref}>
      {props.children}
    </CloseButton>
  </ChakraDialog.CloseTrigger>
));

export const DialogRoot = ChakraDialog.Root;
export const DialogBody = ChakraDialog.Body;
export const DialogBackdrop = ChakraDialog.Backdrop;
export const DialogTitle = ChakraDialog.Title;
export const DialogDescription = ChakraDialog.Description;
export const DialogTrigger = ChakraDialog.Trigger;
export const DialogActionTrigger = ChakraDialog.ActionTrigger;

export interface DialogHeaderProps extends Omit<ChakraDialog.HeaderProps, 'title'> {
  onBack?: () => void;
  onClose?: () => void;
  /** Matches the Figma "Back button" visibility property. */
  showBackButton?: boolean;
  /** Matches the Figma "Close button" visibility property. */
  showCloseButton?: boolean;
  /** Matches the Figma "Label" visibility property. */
  showTitle?: boolean;
  /**
   * Matches the Figma "Title" text property.
   * Use children instead when the header needs custom content.
   */
  title?: React.ReactNode;
}

export function DialogHeader({
  children,
  onBack,
  onClose,
  showBackButton = false,
  showCloseButton = false,
  showTitle = true,
  title,
  ...rest
}: DialogHeaderProps) {
  if (children != null && children !== false) {
    return <ChakraDialog.Header {...rest}>{children}</ChakraDialog.Header>;
  }

  const headerContent =
    showTitle && title !== undefined ? (
      <DialogTitle fontSize="20px" fontWeight="600" lineHeight="28px">
        {title}
      </DialogTitle>
    ) : null;
  const closeButton = (
    <NavigationButton aria-label="Close" size="sm" onClick={onClose}>
      <Close />
    </NavigationButton>
  );
  let closeControl: React.ReactNode = null;

  if (showCloseButton) {
    closeControl = (
      <ChakraDialog.CloseTrigger position="static" asChild>
        {closeButton}
      </ChakraDialog.CloseTrigger>
    );
  }

  return (
    <ChakraDialog.Header
      alignItems="center"
      borderBottomEndRadius="0"
      borderBottomStartRadius="0"
      borderTopRadius="inherit"
      display="flex"
      gap="2"
      minH="20"
      px="4"
      py="6"
      {...rest}
    >
      {showBackButton && onBack !== undefined && (
        <NavigationButton aria-label="Back" size="sm" onClick={onBack}>
          <ArrowBack />
        </NavigationButton>
      )}
      <Box flex="1" minW={0} px="2">
        {headerContent}
      </Box>
      {closeControl}
    </ChakraDialog.Header>
  );
}

export type DialogFooterProps = ChakraDialog.FooterProps;

export function DialogFooter({ children, ...rest }: DialogFooterProps) {
  return (
    <ChakraDialog.Footer borderTopEndRadius="0" borderTopStartRadius="0" {...rest}>
      {children}
    </ChakraDialog.Footer>
  );
}
